import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import client from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { PageHeader, EmptyState, Skeletons, ErrorState } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { ScrollText, Plus, Trash2, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const ACTION_META = {
  created: { label: "Created", icon: Plus, cls: "bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]" },
  deleted: { label: "Deleted", icon: Trash2, cls: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]" },
};

export default function AuditLog() {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const logs = useQuery({
    queryKey: ["audit-logs"],
    queryFn: async () => (await client.get("/audit-logs")).data,
  });

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const rows = (logs.data || []).filter((l) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (l.summary || "").toLowerCase().includes(q) ||
      (l.actor_name || "").toLowerCase().includes(q) ||
      (l.action || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="animate-fade-up">
      <PageHeader title="Audit log" subtitle="Who created or deleted enquiries — visible to admins only" testid="audit-log-header" />

      <div className="relative mb-6 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-testid="audit-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by summary, person or action…"
          className="pl-9"
        />
      </div>

      {logs.isError ? (
        <ErrorState onRetry={() => logs.refetch()} message="We couldn't load the audit log. Please try again." />
      ) : logs.isLoading ? (
        <Skeletons />
      ) : rows.length === 0 ? (
        <EmptyState icon={ScrollText} title={search ? "No matching activity" : "No activity yet"} hint="Enquiry create and delete events will appear here as your team works." />
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="audit-log-list">
          {rows.map((l) => {
            const m = ACTION_META[l.action] || { label: l.action, icon: ScrollText, cls: "bg-muted text-muted-foreground border-border" };
            return (
              <div key={l.id} data-testid={`audit-row-${l.id}`} className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0">
                <span className={cn("inline-flex shrink-0 items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-bold uppercase tracking-wide", m.cls)}>
                  <m.icon className="h-3 w-3" /> {m.label}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-ink">{l.summary || l.entity}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {l.entity} · by <span className="text-ink">{l.actor_name || "team"}</span>
                    {l.actor_role ? <span className="capitalize"> ({l.actor_role})</span> : null}
                  </div>
                </div>
                <div className="shrink-0 text-right text-xs text-muted-foreground">{fmtDate(l.at, "d MMM yyyy, h:mmaaa")}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
